import React from 'react';
import { generatePath } from 'react-router-dom';
import classNames from 'classnames';
import format from 'date-fns/format';
import PropTypes from 'prop-types';
import CampaignLink from '@/components/common/CampaignLink';
import ContributionEnumIcon from '@/components/common/contribution/ContributionEnumIcon';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@/components/common/ResponsiveTable';
import RouterLink from '@/components/common/RouterLink';
import ContributionType from '@/constants/ContributionType';
import SORT_FIELDS from '@/constants/reports/DonationsSortFields';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import translate from '@/lib/intl/translate';
import { useEntityDetails } from '@/modules/EntityDetails';
import { useQueryProvider } from '@/modules/QueryProvider';
import { formatCurrency } from '@/utils/currency';
import { dateStringToDate } from '@/utils/dates';

export default function DonationsDataTable({
  data,
  isLagging,
  showCampaign,
  showConstituent,
}) {
  const { query, updateQuery } = useQueryProvider();
  const { sortBy, sortOrder } = query;
  const { openDonationEntity } = useEntityDetails();

  const onSortClick = React.useCallback(
    (property) => () => updateQuery({ sortBy: property }),
    [updateQuery],
  );

  const handleViewClick = (constituentId, donationId) => {
    openDonationEntity(constituentId, donationId);
  };

  return (
    <TableContainer className="mb-4" data-testid="donations-data-table">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortBy === SORT_FIELDS.DONATION_DATE}
                direction={sortOrder}
                onClick={onSortClick(SORT_FIELDS.DONATION_DATE)}
              >
                {translate('REPORTS_DATE')}
              </TableSortLabel>
            </TableCell>
            <TableCell>{translate('REPORTS_CONTRIBUTION')}</TableCell>
            {showConstituent && (
              <TableCell>
                <TableSortLabel
                  active={sortBy === SORT_FIELDS.DISPLAY_NAME}
                  direction={sortOrder}
                  onClick={onSortClick(SORT_FIELDS.DISPLAY_NAME)}
                >
                  {translate('REPORTS_CONSTITUENT')}
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell>
              <TableSortLabel
                active={sortBy === SORT_FIELDS.DONATION_TYPE}
                direction={sortOrder}
                onClick={onSortClick(SORT_FIELDS.DONATION_TYPE)}
              >
                {translate('REPORTS_TYPE')}
              </TableSortLabel>
            </TableCell>
            {showCampaign && (
              <TableCell>
                <TableSortLabel
                  active={sortBy === SORT_FIELDS.CAMPAIGN_NAME}
                  direction={sortOrder}
                  onClick={onSortClick(SORT_FIELDS.CAMPAIGN_NAME)}
                >
                  {translate('REPORTS_CAMPAIGN')}
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell className="hidden sm:table-cell">
              <TableSortLabel
                active={sortBy === SORT_FIELDS.PAYMENT_TYPE}
                direction={sortOrder}
                onClick={onSortClick(SORT_FIELDS.PAYMENT_TYPE)}
              >
                {translate('REPORTS_METHOD')}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classNames({ 'opacity-60': isLagging })}>
          {data.map(
            ({
              actualValue,
              amountPledged,
              campaignName,
              campaignId,
              constituentId,
              displayName,
              donationDate,
              donationId,
              donationType,
              estimatedValue,
              inKindItemDescription,
              inKindItemQuantity,
              paymentType,
              pledgeAmountReceived,
              timeUnit,
              volunteerTime,
            }) => (
              <TableRow
                className="cursor-pointer hover:bg-grey-100"
                key={donationId}
                onClick={() => handleViewClick(constituentId, donationId)}
              >
                <TableCell label={translate('REPORTS_DATE')}>
                  {format(dateStringToDate(donationDate), 'PP')}
                </TableCell>
                <TableCell label={translate('REPORTS_CONTRIBUTION')}>
                  <div className="flex-col">
                    <div data-testid={`donation-amount-${donationId}`}>
                      {donationType === ContributionType.TIME
                        ? `${volunteerTime} ${timeUnit}`
                        : formatCurrency(
                            pledgeAmountReceived ||
                              actualValue ||
                              estimatedValue,
                            true,
                          )}
                    </div>
                    <div className="text-xs text-text-secondary">
                      <div
                        data-testid={`donation-asssociated-with-pledge-${donationId}`}
                      >
                        {donationType !== ContributionType.PLEDGE &&
                        amountPledged
                          ? translate('REPORTS_PLEDGE_AMOUNT', {
                              amount: formatCurrency(amountPledged, true),
                            })
                          : null}
                      </div>
                      <div
                        data-testid={`pledge-donation-details-${donationId}`}
                      >
                        {donationType === ContributionType.PLEDGE
                          ? translate('DONATION_PLEDGE')
                          : null}
                      </div>
                      <div
                        data-testid={`in-kind-donation-details-${donationId}`}
                      >
                        {donationType === ContributionType.IN_KIND
                          ? ` ${inKindItemQuantity} ${inKindItemDescription}`
                          : null}
                      </div>
                    </div>
                  </div>
                </TableCell>
                {showConstituent && (
                  <TableCell label={translate('REPORTS_CONSTITUENT')}>
                    <RouterLink
                      to={generatePath(AuthRoutes.CONSTITUENT, {
                        constituentId,
                      })}
                    >
                      {displayName}
                    </RouterLink>
                  </TableCell>
                )}
                <TableCell label={translate('REPORTS_TYPE')}>
                  <div className="flex flex-row items-center">
                    <ContributionEnumIcon
                      enumValue={donationType}
                      type={ContributionEnumIcon.donationType}
                    />
                  </div>
                </TableCell>
                {showCampaign && (
                  <TableCell label={translate('REPORTS_CAMPAIGN')}>
                    {campaignId && (
                      <CampaignLink
                        campaignDisplayName={campaignName}
                        campaignId={campaignId}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell
                  className="hidden md:table-cell"
                  label={translate('REPORTS_METHOD')}
                >
                  <div className="flex flex-row items-center">
                    <ContributionEnumIcon
                      enumValue={paymentType}
                      type={ContributionEnumIcon.paymentMethod}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

DonationsDataTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      actualValue: PropTypes.number,
      amountPledged: PropTypes.string,
      campaignId: PropTypes.string,
      campaignName: PropTypes.string,
      constituentId: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      donationDate: PropTypes.string.isRequired,
      donationId: PropTypes.string.isRequired,
      donationSource: PropTypes.string.isRequired,
      donationType: PropTypes.string.isRequired,
      estimatedValue: PropTypes.number,
      inKindItemDescription: PropTypes.string,
      inKindItemQuantity: PropTypes.number,
      paymentType: PropTypes.string,
      pledgeAmountReceived: PropTypes.number,
      timeData: PropTypes.shape({
        time: PropTypes.number,
        timeUnit: PropTypes.string,
      }),
    }),
  ).isRequired,
  isLagging: PropTypes.bool,
  showCampaign: PropTypes.bool,
  showConstituent: PropTypes.bool,
};
DonationsDataTable.defaultProps = {
  isLagging: false,
  showCampaign: true,
  showConstituent: true,
};
